import React, { createContext, useState, useCallback, useEffect } from 'react';
import * as authService from '../services/auth';
import { clearAuthTimers, startProactiveRefreshTimer, startInactivityTimer } from '../services/authInterceptor';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    const initializeAuth = () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const userJson = localStorage.getItem('user');

      if (token && refreshToken && userJson) {
        try {
          const parsedUser = JSON.parse(userJson);
          setUser(parsedUser);
          setIsAuthenticated(true);
          // Start timers if user is already logged in
          startInactivityTimer();
          startProactiveRefreshTimer();
        } catch (error) {
          localStorage.removeItem('user');
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const response = await authService.login(email, password);
      
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        
        // Store tokens
        if (response.token) {
          localStorage.setItem('token', response.token);
        }
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        
        localStorage.setItem('user', JSON.stringify(response.user));
        
        // Start inactivity and proactive refresh timers immediately after successful login
        startInactivityTimer();
        startProactiveRefreshTimer();
        
        return { success: true };
      } else {
        // Ensure error is a string
        const errorMsg = typeof response.error === 'string' ? response.error : String(response.error);
        return { success: false, error: errorMsg };
      }
    } catch (error) {
      // If we get an error here, it means something unexpected happened
      const errorMsg = error.message || String(error) || 'Login failed. Please try again.';
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthTimers();
    setUser(null);
    setIsAuthenticated(false);
    setPendingEmail(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('companyProfile');
    localStorage.removeItem('company_id');
  }, []);

  const register = useCallback(async (email, password, name) => {
    setLoading(true);
    try {
      const response = await authService.register(email, password, name);
      if (response.success) {
        // Store email for OTP verification
        setPendingEmail(email);
        return { success: true, requiresOTP: true };
      } else {
        return { success: false, error: response.error || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOTP = useCallback(async (email, otpCode) => {
    setLoading(true);
    try {
      const response = await authService.verifyOTP(email, otpCode);
      
      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        setPendingEmail(null);
        
        // Store tokens
        if (response.token) {
          localStorage.setItem('token', response.token);
        }
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken);
        }
        
        localStorage.setItem('user', JSON.stringify(response.user));
        
        return { success: true };
      } else {
        return { success: false, error: response.error || 'OTP verification failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const resendOTP = useCallback(async (email) => {
    try {
      const response = await authService.resendOTP(email);
      return response;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const value = {
    user,
    isAuthenticated,
    loading,
    pendingEmail,
    login,
    logout,
    register,
    verifyOTP,
    resendOTP,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
